<template>
  <b-modal
    id="setup-step-events"
    ref="setup-step-events"
    centered
    size="xl"
    title="Set up step event"
    ok-title="Save"
    cancel-variant="outline-secondary"
    no-close-on-backdrop
    :ok-disabled="!canUpdateApplicationTriggers"
    @show="loadData"
    @hidden="resetModalData"
    @ok="postStepTriggers"
  >
    <b-overlay
      id="overlay-background"
      :show="isLoading"
      variant="white"
      opacity="1"
      rounded="sm"
    >
      <validation-observer
        ref="setupStepEvents"
        tag="form"
      >
        <template
          v-for="(stepTriggerRow, index) in filteredStepTriggers"
        >
          <b-row :key="`step-event-${index}`">
            <b-col
              cols="11"
            >
              <b-row>
                <!--  Step  -->
                <b-col
                  sm="3"
                  class="mb-1"
                >
                  <b-form-group
                    label="Step"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="step"
                      rules="required"
                    >
                      <v-select
                        v-model="stepTriggerRow.for_id"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="stepsList"
                        disabled
                        placeholder="Select Step"
                        :clearable="false"
                        value="id"
                        label="full_name"
                        :reduce="value => value.id"
                        :class="{'is-invalid': errors.length > 0 }"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!--  Event  -->
                <b-col
                  sm="3"
                  class="mb-1"
                >
                  <b-form-group
                    label="Event"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="event"
                      rules="required"
                    >
                      <v-select
                        v-model="stepTriggerRow.event_class"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="eventsOptions"
                        :placeholder="'Select Event'"
                        :clearable="false"
                        value="event"
                        label="name"
                        :reduce="value => value.event"
                        :class="{'is-invalid': errors.length > 0 }"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!--  Trigger  -->
                <b-col
                  sm="3"
                  class="mb-1"
                >
                  <b-form-group
                    label="Trigger"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="trigger"
                      rules="required"
                    >
                      <v-select
                        v-model="stepTriggerRow.trigger"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="triggersOptions"
                        :placeholder="'Select Trigger'"
                        :clearable="false"
                        :reduce="value => value.value"
                        :class="{'is-invalid': errors.length > 0 }"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!--  Email Template  -->
                <b-col
                  v-if="stepTriggerRow.trigger === 'Email' || stepTriggerRow.trigger == null"
                  sm="3"
                  class="mb-1"
                >
                  <b-form-group
                    label="Email Template"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="email template"
                      rules="required"
                    >
                      <v-select
                        v-model="stepTriggerRow.email_template_id"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="emailTemplatesOptions"
                        :clearable="false"
                        :placeholder="'Select Email Template'"
                        value="id"
                        label="name"
                        :reduce="val => val.id"
                        :class="{'is-invalid': errors.length > 0 }"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <!-- Rich Text field  -->
                <b-col
                  v-else
                  sm="3"
                  class="mb-1"
                >
                  <b-form-group
                    label="Text"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="text"
                      rules="required"
                    >
                      <quill-editor
                        id="email-content"
                        v-model="stepTriggerRow.message_text"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>

            <!-- Input Delete -->
            <b-col
              sm="1"
              class="align-self-center"
            >
              <SquareIcon
                v-if="canDeleteApplicationTriggers"
                size="21"
                icon="Trash2Icon"
                class="cursor-pointer"
                @onIconClick="removeRow(stepTriggerRow.frontID)"
              />
            </b-col>
          </b-row>
        </template>

        <b-row>
          <b-col
            sm="3"
          >
            <b-button
              variant="outline-primary"
              class="d-flex align-items-center justify-content-center w-100 mt-2"
              :disabled="!canCreateApplicationTriggers"
              @click="addNewStepTriggerRow"
            >
              <feather-icon
                icon="PlusIcon"
                size="18"
                class="mr-1"
              />
              Add Trigger
            </b-button>
          </b-col>
        </b-row>
      </validation-observer>
      <div
        v-if="validationErrors.length"
        class="form-group"
      >
        <p
          v-for="(validationError, index) in validationErrors"
          :key="index"
          class="text-center text-danger"
        >
          {{ validationError }}
        </p>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  BButton,
  BFormGroup,
  BRow,
  BCol,
  BOverlay,
} from 'bootstrap-vue'
import { quillEditor } from 'vue-quill-editor'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import SquareIcon from '@/views/ui/icon/SquareIcon.vue'
import useTriggersSetting from '@/views/admin/program/program-communication-list/useTriggersSetting'

export default {
  name: 'StepTriggersSettingsModal',
  components: {
    BButton,
    BFormGroup,
    BRow,
    BCol,
    BOverlay,

    ValidationObserver,
    ValidationProvider,
    vSelect,
    SquareIcon,
    quillEditor,
  },
  data() {
    return {
      rowsCount: 0,
      stepTriggers: [],
      eventsOptions: [],
      emailTemplatesOptions: [],
      triggersOptions: [],
      validationErrors: [],
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      programId: 'app-program/getProgramId',
      selectedStep: 'app-program/getSelectedStep',
      stepsList: 'app-program/getProgramStepsList',
    }),
    filteredStepTriggers() {
      return this.stepTriggers.filter(step => !step.delete)
    },
  },
  setup() {
    const {
      fetchProgramTriggers,
      fetchEvents,
      fetchTriggers,
      fetchEmailTemplates,
      saveTriggers,
      canCreateApplicationTriggers,
      canUpdateApplicationTriggers,
      canDeleteApplicationTriggers,
    } = useTriggersSetting()

    return {
      fetchProgramTriggers,
      fetchEvents,
      fetchTriggers,
      fetchEmailTemplates,
      saveTriggers,
      canCreateApplicationTriggers,
      canUpdateApplicationTriggers,
      canDeleteApplicationTriggers,
    }
  },
  methods: {
    async loadData() {
      this.isLoading = true
      await this.setStepTriggersData()
      await this.fetchEvents().then(response => {
        this.eventsOptions = response
      })
      await this.fetchTriggers().then(response => {
        this.triggersOptions = response.map(item => (
          {
            value: item,
            label: item,
          }
        ))
      })
      await this.fetchEmailTemplates().then(response => {
        this.emailTemplatesOptions = response
      })
      this.isLoading = false
    },
    resetModalData() {
      this.rowsCount = 0
      this.stepTriggers = []
      this.eventsOptions = []
      this.emailTemplatesOptions = []
      this.triggersOptions = []
      this.validationErrors = []
      this.isLoading = false
    },
    async setStepTriggersData() {
      const queryParams = {
        programId: this.programId,
        stepId: this.selectedStep.id,
      }
      await this.fetchProgramTriggers(queryParams)
        .then(response => {
          this.stepTriggers = response.map((item, index) => (
            {
              frontID: index,
              delete: false,
              ...item,
            }
          ))

          this.rowsCount = this.stepTriggers.length
        })
    },
    isFrontIDExists(key) {
      return this.stepTriggers.some(field => field.frontID === key)
    },
    addNewStepTriggerRow() {
      do {
        this.rowsCount += 1
      } while (this.isFrontIDExists(this.rowsCount))

      this.stepTriggers.push({
        id: null,
        event_class: null,
        type: 'Step',
        for_id: this.selectedStep.id,
        program_id: this.programId,
        trigger: null,
        email_template_id: null,
        delete: false,
        frontID: this.rowsCount,
        message_text: null,
        newField: true,
      })
    },
    removeRow(ID) {
      const curItem = this.stepTriggers.findIndex(item => item.frontID === ID)
      this.stepTriggers[curItem].delete = true

      if (this.stepTriggers[curItem].newField) {
        this.stepTriggers.splice(curItem, 1)
      }
    },
    async postStepTriggers(bvModalEvt) {
      bvModalEvt.preventDefault()

      // eslint-disable-next-line consistent-return
      return new Promise(reject => {
        this.$refs.setupStepEvents.validate().then(success => {
          if (success) {
            this.saveTriggers(this.stepTriggers)
              .then(response => {
                if (response.data && response.status === 422) {
                  const validationErrors = Object.values(response.data.errors)
                  this.validationErrors = validationErrors.flat()
                } else {
                  this.validationErrors = []
                  this.$nextTick(() => {
                    this.$bvModal.hide('setup-step-events')
                  })
                }
              })
          } else {
            reject()
          }
        })
      })
    },
  },
}
</script>

<style scoped>

</style>
